import type {
  IStudentDetailState,
  ITeacherStudentDetailApiResponse,
} from '@/types/class';

import React, { useState, useEffect } from 'react';

import { IconStar, IconMoodKid, IconCalendarEvent } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import ProgressBar from '@/components/ProgressBar';
import StudentDetailDrawerSkeleton from '@/components/SkeletonLoader/StudentDetailDrawerSkeleton';
import { useFormatText } from '@/hooks/useFormatText';
import { getTeacherClassStudentDetail } from '@/service/studentService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, formatUnixToLongDate } from '@/utils';

interface IStudentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string | null;
  classId?: string;
}

export const StudentDetailDrawer: React.FC<IStudentDetailDrawerProps> = ({
  isOpen,
  onClose,
  studentId,
  classId,
}) => {
  const { formatText } = useFormatText();
  const { isDrawerLoading, setDrawerLoading } = useSkeletonStore();
  const [studentDetail, setStudentDetail] =
    useState<IStudentDetailState | null>(null);

  useEffect(() => {
    const fetchStudentDetail = async () => {
      if (!isOpen || !studentId || !classId) return;
      setDrawerLoading(true);
      try {
        const res = await getTeacherClassStudentDetail(classId, studentId);
        if (res?.status && res?.data) {
          const detail = res.data as ITeacherStudentDetailApiResponse;

          setStudentDetail({
            id: detail.id,
            full_name: detail.full_name,
            profile_pic: getAssetUrl(detail?.profile_pic),
            class_name: detail.class_name,
            device_name: detail.device_name || '-',
            age: detail.age ? String(detail.age) : null,
            gender: detail.gender || '-',
            date_of_birth: detail.date_of_birth
              ? formatUnixToLongDate(detail.date_of_birth)
              : '-',
            current_progress: detail.learning_overview?.current_progress ?? 0,
            stars_earned: detail.learning_overview?.stars_earned ?? 0,
            learning_modules: Array.isArray(detail.learning_modules)
              ? detail.learning_modules.map((module) => ({
                  id: module.lesson_id,
                  name: module.title,
                  games: module.total_games,
                  progress: module.progress,
                }))
              : [],
          });
        } else {
          setStudentDetail(null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDrawerLoading(false);
      }
    };

    fetchStudentDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, studentId, classId]);

  if (!isOpen || !studentId || !classId) return null;

  return (
    <CustomModal
      setIsOpen={onClose}
      heading={formatText('studentDetail.title')}
      isHeader
      className="max-w-150"
      position="right"
      childClassName="p-6 space-y-10"
      dataTestId="drawer"
    >
      {isDrawerLoading || !studentDetail ? (
        <div className="w-full">
          <StudentDetailDrawerSkeleton />
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center gap-4 w-full">
            <UserAvatarList
              user={studentDetail?.full_name}
              image={studentDetail?.profile_pic || ''}
              className="w-25 h-25 text-3xl!"
            />

            <h2 className="text-2xl font-bold text-neutral-primary text-center capitalize">
              {studentDetail?.full_name}
            </h2>

            <div className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50">
              <span className="text-xs font-semibold text-success-secondary">
                {studentDetail?.class_name}
              </span>
            </div>

            <div className="flex items-center justify-center gap-8.75 w-full text-sm">
              <div className="flex items-center gap-1">
                <span className="text-neutral-secondary">
                  {formatText('studentDetail.age')}:
                </span>
                {studentDetail?.age ? (
                  <span className="text-neutral-primary">
                    {studentDetail?.age} {formatText('studentDetail.years')}
                  </span>
                ) : (
                  '-'
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-neutral-secondary">
                  {formatText('studentDetail.gender')}:
                </span>
                <span className="text-neutral-primary capitalize">
                  {studentDetail?.gender}
                </span>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 w-full">
            <div className="flex items-start gap-2 flex-1">
              <IconMoodKid size={24} className="text-neutral-secondary mt-1" />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('studentDetail.fullName')}
                </p>
                <p className="text-base text-neutral-primary max-w-55 wrap-anywhere capitalize">
                  {studentDetail?.full_name}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 flex-1">
              <IconCalendarEvent
                size={24}
                className="text-neutral-secondary mt-1"
              />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('studentDetail.dateOfBirth')}
                </p>

                <span className="text-base font-medium text-neutral-primary">
                  {studentDetail?.date_of_birth}
                  {studentDetail?.age && (
                    <span className="text-neutral-secondary text-sm ml-1">
                      ({studentDetail?.age} {formatText('studentDetail.years')})
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full">
            <h3 className="text-lg font-semibold text-neutral-primary">
              {formatText('studentDetail.learningOverview.title')}
            </h3>

            <div className="border border-neutral-200 rounded-2xl p-4 space-y-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-base">
                  <span className="text-neutral-secondary">
                    {formatText(
                      'studentDetail.learningOverview.currentProgress'
                    )}
                  </span>
                  <span className="font-semibold text-neutral-primary">
                    {studentDetail?.current_progress}%
                  </span>
                </div>
                <ProgressBar
                  value={studentDetail?.current_progress}
                  showPercentage={false}
                  height="h-2"
                />
              </div>

              <div className="flex gap-6">
                <div className="flex-1 bg-yellow-100 rounded-lg px-4 py-2 flex flex-col items-center justify-center h-18">
                  <p className="text-xs font-semibold text-warning-secondary">
                    {formatText('studentDetail.learningOverview.starsEarned')}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <IconStar
                      size={24}
                      className="text-yellow-500 fill-yellow-500"
                    />
                    <span className="text-2xl font-semibold text-warning-primary">
                      {studentDetail?.stars_earned}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full">
            <h3 className="text-lg font-semibold text-neutral-primary">
              {formatText('studentDetail.learningModules.title')}
            </h3>

            <div className="space-y-4">
              {studentDetail?.learning_modules?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <CustomImage
                    src={'/assets/images/lesson-breakdown-empty.png'}
                    alt="noData"
                    width={100}
                    height={100}
                  />
                  <p className="text-neutral-900 font-semibold">
                    {formatText('common.no-data-found')}
                  </p>
                </div>
              ) : (
                studentDetail?.learning_modules?.map((lesson) => (
                  <div
                    key={lesson.id}
                    className="border border-neutral-200 rounded-2xl p-4"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-end justify-between">
                        <div className="flex flex-col gap-1">
                          <p className="text-base font-semibold text-neutral-primary">
                            {lesson.name}
                          </p>
                          <p className="text-base text-neutral-secondary">
                            {lesson.games}{' '}
                            {formatText('studentDetail.learningModules.games')}
                          </p>
                        </div>
                        <span className="text-lg font-semibold text-neutral-primary">
                          {lesson.progress}%
                        </span>
                      </div>
                      <ProgressBar
                        value={lesson.progress}
                        showPercentage={false}
                        height="h-2"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </CustomModal>
  );
};
